// Role definitions and permissions

export type UserRole = 
  | 'super_admin'
  | 'administrador_gerencial' 
  | 'administrador_recepcionista' 
  | 'profesional' 
  | 'cliente_interno';

export interface User {
  id: string;
  email: string;
  username?: string;
  full_name?: string;
  role: UserRole;
  /** Identificador primario de tenant. Usar en todas las queries nuevas. */
  organization_id?: string | null;
  /**
   * @deprecated Identificador legacy de tenant.
   * Mantener por compatibilidad con ~231 queries existentes.
   * Migración completa pendiente: ver tarea 4.6 en ROADMAP-MESTLY.md.
   * Para código nuevo usar organization_id vía getOrgId() en src/lib/tenant.ts.
   */
  salon_id?: string;
  is_active: boolean;
  must_change_password?: boolean;
  created_at: string;
  updated_at: string;
}

export interface Permission {
  module: string;
  actions: {
    read: boolean;
    create: boolean;
    update: boolean;
    delete: boolean;
    download: boolean;
  };
}

export type PermissionAction = keyof Permission['actions'];
export type RolePermissionMap = Record<string, Permission['actions']>;

// Define permissions for each role
export const rolePermissions: Record<UserRole, RolePermissionMap> = {
  super_admin: {
    appointments: { read: true, create: true, update: true, delete: true, download: true },
    clients: { read: true, create: true, update: true, delete: true, download: true },
    crm: { read: true, create: true, update: true, delete: true, download: true },
    loyalty: { read: true, create: true, update: true, delete: true, download: true },
    professionals: { read: true, create: true, update: true, delete: true, download: true },
    services: { read: true, create: true, update: true, delete: true, download: true },
    inventory: { read: true, create: true, update: true, delete: true, download: true },
    finances: { read: true, create: true, update: true, delete: true, download: true },
    reports: { read: true, create: true, update: true, delete: true, download: true },
    settings: { read: true, create: true, update: true, delete: true, download: true },
    users: { read: true, create: true, update: true, delete: true, download: true },
  },
  administrador_gerencial: {
    appointments: { read: true, create: true, update: true, delete: true, download: true },
    clients: { read: true, create: true, update: true, delete: true, download: true },
    crm: { read: true, create: true, update: true, delete: true, download: true },
    loyalty: { read: true, create: true, update: true, delete: true, download: false },
    professionals: { read: true, create: true, update: true, delete: true, download: true },
    services: { read: true, create: true, update: true, delete: true, download: true },
    inventory: { read: true, create: true, update: true, delete: true, download: true },
    finances: { read: true, create: true, update: true, delete: true, download: true },
    reports: { read: true, create: false, update: false, delete: false, download: true },
    settings: { read: true, create: true, update: true, delete: true, download: false },
    users: { read: true, create: true, update: true, delete: true, download: false },
  },
  administrador_recepcionista: {
    appointments: { read: true, create: true, update: true, delete: false, download: false },
    clients: { read: true, create: true, update: true, delete: true, download: false },
    crm: { read: false, create: false, update: false, delete: false, download: false },
    loyalty: { read: true, create: false, update: false, delete: false, download: false },
    professionals: { read: true, create: false, update: false, delete: false, download: false },
    services: { read: true, create: false, update: false, delete: false, download: false },
    inventory: { read: true, create: true, update: true, delete: false, download: false },
    finances: { read: true, create: true, update: true, delete: false, download: false },
    reports: { read: false, create: false, update: false, delete: false, download: false },
    settings: { read: false, create: false, update: false, delete: false, download: false },
    users: { read: false, create: false, update: false, delete: false, download: false },
  },
  profesional: {
    appointments: { read: true, create: false, update: false, delete: false, download: false },
    clients: { read: false, create: false, update: false, delete: false, download: false },
    crm: { read: false, create: false, update: false, delete: false, download: false },
    loyalty: { read: false, create: false, update: false, delete: false, download: false },
    professionals: { read: false, create: false, update: false, delete: false, download: false },
    services: { read: false, create: false, update: false, delete: false, download: false },
    inventory: { read: false, create: false, update: false, delete: false, download: false },
    finances: { read: false, create: false, update: false, delete: false, download: false },
    reports: { read: false, create: false, update: false, delete: false, download: false },
    settings: { read: false, create: false, update: false, delete: false, download: false },
    users: { read: false, create: false, update: false, delete: false, download: false },
  },
  cliente_interno: {
    appointments: { read: false, create: false, update: false, delete: false, download: false },
    clients: { read: false, create: false, update: false, delete: false, download: false },
    crm: { read: false, create: false, update: false, delete: false, download: false },
    loyalty: { read: false, create: false, update: false, delete: false, download: false },
    professionals: { read: false, create: false, update: false, delete: false, download: false },
    services: { read: false, create: false, update: false, delete: false, download: false },
    inventory: { read: false, create: false, update: false, delete: false, download: false },
    finances: { read: false, create: false, update: false, delete: false, download: false },
    reports: { read: false, create: false, update: false, delete: false, download: false },
    settings: { read: false, create: false, update: false, delete: false, download: false },
    users: { read: false, create: false, update: false, delete: false, download: false },
  },
};

export const roleLabels: Record<UserRole, string> = {
  super_admin: 'Super Administrador',
  administrador_gerencial: 'Administrador Gerencial',
  administrador_recepcionista: 'Administrador Recepcionista',
  profesional: 'Profesional',
  cliente_interno: 'Cliente Interno',
};

export const roleDescriptions: Record<UserRole, string> = {
  super_admin: 'Acceso total irrestricto - Usuario maestro del sistema',
  administrador_gerencial: 'Acceso completo a todo el sistema',
  administrador_recepcionista: 'Gestión de citas, clientes e ingresos/egresos',
  profesional: 'Visualización de su propia agenda',
  cliente_interno: 'Sin acceso al sistema (solo registro interno)',
};
